import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {DESIGNER_SITE_MODES} from '@packages/systems/design-system/constants/DesignerUI';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

/**
 * Determines whether or not the user can access the design site mode.
 *
 * If the user is authorized in the designer, they will have a site role.
 * The site role's permissions will determine whether or not they can access the design site mode.
 *
 * If the user is unauthorized in the designer, they will be able to access the design site mode so they
 * can "play around" with the design. Unauthorized users are placed in playground (READ_ONLY) mode.
 */
export const canAccessDesignSiteMode = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    branches: {
      main: true,
    },
    permissions: [
      [DESIGNER_SITE_MODES.DESIGN, 'siteDesignerModes'],
      ['design', 'site'],
    ],
  },
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    branches: {
      branch: true,
    },
    permissions: [
      [DESIGNER_SITE_MODES.DESIGN, 'siteDesignerModes'],
      ['design', 'siteBranch'],
    ],
  },
  {
    // A Release (site-branch) page resolves as `main` in the branches
    // dimension, so it needs its own clause for roles that can design in
    // branches but not on the main site.
    // Once the branches dimension resolves Releases as `branch`, the branch
    // clause above will match them with the flag off too, and this clause
    // becomes redundant.
    featureFlags: {
      [FEATURE_FLAGS.SITE_BRANCHES]: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    pageScope: ['siteBranch'],
    permissions: [
      [DESIGNER_SITE_MODES.DESIGN, 'siteDesignerModes'],
      ['design', 'siteBranch'],
    ],
  },
  {
    userTypes: {unauthorized: true},
  },
] as const);

export const canAccessEditSiteMode = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    branches: {
      main: true,
    },
    permissions: [
      [DESIGNER_SITE_MODES.EDIT, 'siteDesignerModes'],
      ['design', 'site'],
    ],
  },
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    branches: {
      branch: true,
    },
    permissions: [
      [DESIGNER_SITE_MODES.EDIT, 'siteDesignerModes'],
      ['design', 'siteBranch'],
    ],
  },
  {
    // Release clause, as in canAccessDesignSiteMode.
    featureFlags: {
      [FEATURE_FLAGS.SITE_BRANCHES]: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    pageScope: ['siteBranch'],
    permissions: [
      [DESIGNER_SITE_MODES.EDIT, 'siteDesignerModes'],
      ['design', 'siteBranch'],
    ],
  },
  {
    // Read-only Preview visitors can use Edit mode locally, while
    // server-enforced playground mode prevents their changes from persisting.
    userTypes: {unauthorized: true},
  },
] as const);

export const canAccessViewSiteMode = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    anonymousComment: true,
  },
  branches: {
    main: true,
  },
  permission: [DESIGNER_SITE_MODES.VIEW, 'siteDesignerModes'],
} as const);
